import { Link } from "react-router"

interface ActivityItem {
  id: string
  title: string
  body: string | null
  actorName: string | null
  timestamp: string
  href?: string
}

interface ActivityFeedProps {
  items: ActivityItem[]
}

export function ActivityFeed({ items }: ActivityFeedProps) {
  return (
    <section className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
      <div className="flex items-end justify-between gap-4 border-b border-gold-500/50 px-5 py-5 sm:px-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-forest-700/70">Activity log</p>
          <h3 className="mt-1 font-display text-xl font-semibold text-ink">Recent activity</h3>
        </div>
        <p className="hidden text-right text-xs text-ink/45 sm:block">The latest updates across your organization</p>
      </div>

      <div className="px-5 py-2 sm:px-6">
        {items.length === 0 ? (
          <div className="px-2 py-10 text-center">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-cream-50 text-forest-700">
              <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-forest-700" />
            </div>
            <p className="mt-4 text-sm font-medium text-ink">No recent activity yet</p>
            <p className="mx-auto mt-1 max-w-xs text-sm leading-6 text-ink/50">
              New submissions, reviews, and admin actions will appear here.
            </p>
          </div>
        ) : (
          <ul className="relative">
            <span aria-hidden="true" className="absolute bottom-7 left-[7px] top-7 w-px bg-forest-700/15" />
            {items.map((item) => (
              <li key={item.id} className="group relative flex gap-4 py-4 first:pt-5 last:pb-5">
                <span aria-hidden="true" className="relative z-10 mt-1.5 h-3.5 w-3.5 flex-none rounded-full border-[3px] border-white bg-forest-700" />
                <div className="min-w-0 flex-1">
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="block rounded-lg px-2 py-1.5 -my-1.5 text-sm font-medium text-ink transition-colors hover:bg-cream-50 hover:text-forest-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-700/30"
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <p className="px-2 py-1.5 -my-1.5 text-sm font-medium text-ink">{item.title}</p>
                  )}
                  {item.body && <p className="px-2 pt-1 text-sm text-ink/65">{item.body}</p>}
                  <p className="px-2 pt-1 text-xs text-ink/45">{item.actorName ? `${item.actorName} · ` : ""}{item.timestamp}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
